'use client';

import React, { useEffect, useRef } from 'react';
import { useTimer } from 'react-timer-hook';
import { useTranslations } from 'next-intl';
import { twJoin } from 'tailwind-merge';
import { useIsHydrated } from '@/ui/hooks/useIsHydrated';
import { pad } from '@/utils/number-helper';

interface BannerCountdownProps {
  expiryDate: Date;
  onExpire?: () => void;
  className?: string;
}

export function BannerCountdown({
  expiryDate,
  onExpire,
  className,
}: Readonly<BannerCountdownProps>) {
  const t = useTranslations('Banner');
  const isHydrated = useIsHydrated();
  const hasExpiredRef = useRef(false);

  const handleExpire = () => {
    if (hasExpiredRef.current) {
      return;
    }

    hasExpiredRef.current = true;
    onExpire?.();
  };

  const {
    days, hours, minutes, seconds,
  } = useTimer({
    expiryTimestamp: expiryDate,
    autoStart: true,
    onExpire: handleExpire,
  });

  // react-timer-hook only fires onExpire on the tick that reaches zero, so an already-past date
  // needs to be handled explicitly.
  useEffect(() => {
    if (expiryDate.getTime() <= Date.now()) {
      handleExpire();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const segments: Array<{ label: string; value: number }> = [
    { label: t('days'), value: days },
    { label: t('hours'), value: hours },
    { label: t('minutes'), value: minutes },
    { label: t('seconds'), value: seconds },
  ];

  // Only expose the live time in the label after hydration; the values differ between the server
  // render and the client, so building it eagerly causes an aria-label hydration mismatch.
  const timeLabel = isHydrated
    ? segments.map((segment) => `${pad(segment.value)} ${segment.label}`).join(' ')
    : undefined;

  return (
    <div className={twJoin('flex items-start gap-3', className)} aria-label={timeLabel}>
      {segments.map((segment) => (
        <div
          key={segment.label}
          className="flex flex-col items-center justify-center leading-tight"
        >
          {/* Before hydration, render a stable, invisible placeholder so the digit box reserves
              the same space as the real numbers (identical server/client markup → no hydration
              mismatch; same box → no layout shift when the live values appear). */}
          <span className={twJoin('text-3xl font-bold tabular-nums', !isHydrated && 'invisible')}>
            {isHydrated ? pad(segment.value) : '00'}
          </span>
          <span className="text-sm opacity-70">{segment.label}</span>
        </div>
      ))}
    </div>
  );
}
